import { APP_BOOTSTRAP_LISTENER, NgZone, PLATFORM_ID, Provider, inject } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';

/**
 * OB-7718 фаза 7: затычка нативной утечки Chromium с lazy-изображениями.
 *
 * <img loading="lazy">, отсоединённый от документа ДО начала загрузки
 * (ниже-фолдовые списки при SPA-навигации), навсегда остаётся в blink
 * «Pending activities» как ActiveScriptWrappable и через цепочку parentNode
 * нативно удерживает ВСЁ отсоединённое дерево уничтоженной страницы —
 * независимо от JS-ссылок (подтверждено heap-снапшотами: единственный
 * retainer-путь — C++ Persistent roots → Pending activities → <img> →
 * родители). ~2МБ на цикл из 4 навигаций.
 *
 * Лечение: наблюдаем удаления DOM; отсоединённый img-якорь ПОДМЕНЯЕМ свежим
 * стабом без src/srcset/sizes (оригинальные значения — в data-x-reaped стаба).
 * Просто снять атрибуты недостаточно: у отсоединённого img blink не пересматривает
 * source selection, и он остаётся зарегистрирован в MediaQueryMatcher документа
 * через HTMLImageElement::ViewportChangeListener (второй нативный якорь, тоже
 * держит дерево через parentNode). После подмены якорь-оригинал повисает
 * одиночным элементом (~сотни байт), а дерево страницы освобождается. Если узел возвращается в DOM (кэш вью,
 * header-storage, vc.move) — атрибуты восстанавливаются по событию добавления.
 * Реап отложен на таску (500мс): временные detach/attach (перестановки
 * ViewContainerRef, noindex-обёртки) не задеваются — к моменту реапа они
 * снова isConnected. Всё вне Angular-зоны: мутации не должны дёргать CD.
 */
export function provideLazyImgReaper(): Provider {
    return {
        provide: APP_BOOTSTRAP_LISTENER,
        multi: true,
        useFactory: () => {
            const pid = inject(PLATFORM_ID);
            const doc = inject<Document>(DOCUMENT);
            const zone = inject(NgZone);
            return () => {
                if (!isPlatformBrowser(pid)) {
                    return;
                }
                (window as any)['obImgReap'] = 0; // маркер сборки/диагностики
                zone.runOutsideAngular(() => {
                    const pend: Set<any> = new Set();
                    let t: any = null;
                    // стаб — инертный <span> (НЕ <img>: свежий img внутри
                    // отсоединённого <picture> с <source media> сам бы
                    // зарегистрировал viewport-listener при вставке)
                    const restore = (stub: any) => {
                        const state = stub.getAttribute('data-x-reaped');
                        if (!state || !stub.parentNode) {
                            return;
                        }
                        try {
                            const img = doc.createElement('img');
                            for (const [name, value] of JSON.parse(state)) {
                                img.setAttribute(name, value);
                            }
                            stub.parentNode.replaceChild(img, stub);
                        } catch (e) {}
                    };
                    const reap = () => {
                        t = null;
                        let n = 0;
                        for (const img of pend) {
                            if (img.isConnected || !img.parentNode) {
                                continue;
                            }
                            const responsive = img.hasAttribute('srcset') || img.hasAttribute('sizes')
                                || (img.parentElement && img.parentElement.localName === 'picture');
                            const pending = !img.complete && img.hasAttribute('src');
                            if (!responsive && !pending) {
                                continue;
                            }
                            const stub = doc.createElement('span');
                            stub.style.display = 'none';
                            stub.setAttribute('data-x-reaped', JSON.stringify(
                                (Array.from(img.attributes) as any[]).map(a => [a.name, a.value])
                            ));
                            img.parentNode.replaceChild(stub, img);
                            // оригинал повисает якорем-одиночкой: рвём его ссылки
                            img.onload = img.onerror = null;
                            img.removeAttribute('src');
                            img.removeAttribute('srcset');
                            img.removeAttribute('sizes');
                            n++;
                        }
                        pend.clear();
                        (window as any)['obImgReap'] += n;
                    };
                    const collect = (node: any) => {
                        if (node.nodeType !== 1) {
                            return;
                        }
                        if (node.localName === 'img') {
                            pend.add(node);
                        }
                        node.querySelectorAll && node.querySelectorAll('img')
                            .forEach((img: any) => pend.add(img));
                    };
                    const mo = new MutationObserver((recs) => {
                        for (const rec of recs) {
                            for (const node of rec.removedNodes as any) {
                                collect(node);
                            }
                            for (const node of rec.addedNodes as any) {
                                if (node.nodeType !== 1) {
                                    continue;
                                }
                                if (node.localName === 'span' && node.hasAttribute('data-x-reaped')) {
                                    restore(node);
                                }
                                node.querySelectorAll && node.querySelectorAll('span[data-x-reaped]')
                                    .forEach(restore);
                            }
                        }
                        if (pend.size && t === null) {
                            t = setTimeout(reap, 500);
                        }
                    });
                    mo.observe(doc.body, { childList: true, subtree: true });
                });
            };
        }
    };
}
